<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Tổng hợp thưởng AdHoc theo chương trình'">
          <template v-slot:preview>
            <div class="dash-filter mb-5">
              <b-row class="align-items-end">
                <b-col md="4">
                  <label class="dash-filter__label">Chương trình</label>
                  <b-input
                    size="sm"
                    v-model="filter.keyword"
                    placeholder="Tìm theo tên chương trình"
                    autocomplete="off"
                    @keyup.enter="fetchPrograms"
                  ></b-input>
                </b-col>
                <b-col md="2">
                  <label class="dash-filter__label">Trạng thái</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.status"
                    :options="statusOptions"
                    value-field="id"
                    text-field="name"
                    @change="fetchPrograms"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >Tất cả</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-6"
                    :disabled="onLoading"
                    @click="fetchPrograms"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <!-- Tổng toàn bộ chương trình đang hiển thị -->
            <div class="dash-summary mb-5">
              <div
                v-for="card in summaryCards"
                :key="card.key"
                class="dash-summary__card"
                :class="
                  card.variant ? 'dash-summary__card--' + card.variant : ''
                "
              >
                <div class="dash-summary__label">{{ card.label }}</div>
                <div class="dash-summary__value">{{ card.value }}</div>
              </div>
            </div>

            <b-table
              :items="programs"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              empty-text="Không có chương trình AdHoc nào"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
              </template>
              <template v-slot:cell(name)="row">
                <div class="font-weight-bold text-dark">
                  {{ row.item.name }}
                </div>
                <div class="text-muted small">
                  {{ displayDate(row.item.startDate) }} —
                  {{
                    row.item.endDate
                      ? displayDate(row.item.endDate)
                      : 'không giới hạn'
                  }}
                </div>
              </template>
              <template v-slot:cell(status)="row">
                <span
                  class="label label-inline font-weight-bold"
                  :class="statusVariant(row.item.status)"
                  >{{ statusName(row.item.status) }}</span
                >
              </template>
              <template v-slot:cell(budget)="row">
                <span v-if="row.item.budget">{{
                  displayAmount(row.item.budget)
                }}</span>
                <span v-else class="text-muted">không giới hạn</span>
              </template>
              <template v-slot:cell(spentAmount)="row">
                <span class="font-weight-bold">{{
                  displayAmount(row.item.spentAmount)
                }}</span>
              </template>
              <template v-slot:cell(usage)="row">
                <template v-if="row.item.budgetUsedPercent !== null">
                  <b-progress
                    height="6px"
                    :value="Math.min(row.item.budgetUsedPercent, 100)"
                    :variant="budgetVariant(row.item.budgetStatus)"
                  ></b-progress>
                  <div class="d-flex justify-content-between mt-1 small">
                    <b-badge
                      v-if="budgetStatusName(row.item.budgetStatus)"
                      :variant="budgetVariant(row.item.budgetStatus)"
                      >{{ budgetStatusName(row.item.budgetStatus) }}</b-badge
                    >
                    <span v-else class="text-success">Trong ngân sách</span>
                    <span>{{ row.item.budgetUsedPercent }}%</span>
                  </div>
                </template>
                <span v-else class="text-muted small">Chưa đặt ngân sách</span>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 dash-link mr-3"
                  @click="openProgram(row.item)"
                  >Theo cửa hàng</b-button
                >
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 dash-link"
                  :disabled="exportingId === row.item.id"
                  @click="exportProgram(row.item)"
                >
                  <i class="far fa-file-excel mr-1"></i>Excel
                </b-button>
              </template>
            </b-table>
            <b-pagination
              v-show="totalPages > 1"
              v-model="page"
              :total-rows="totalItems"
              :per-page="PAGE_LIMIT"
              align="right"
              @change="onPageChange"
            ></b-pagination>
            <div class="text-muted small">
              "Đã chi" là tổng thưởng AdHoc của các kỳ đã chốt sổ.
            </div>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Đã chi của một chương trình theo cửa hàng nhận -->
    <b-modal
      v-model="detailVisible"
      :title="detail ? detail.name : 'Chi tiết chương trình'"
      size="xl"
      ok-only
      ok-title="Đóng"
      ok-variant="secondary"
    >
      <vcl-table v-if="detailLoading" :columns="4"></vcl-table>
      <template v-else-if="detail">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            Đã chi <strong>{{ displayAmount(detail.spentAmount) }}</strong>
            <template v-if="detail.budget">
              / ngân sách {{ displayAmount(detail.budget) }} ({{
                detail.budgetUsedPercent
              }}%)
            </template>
            · {{ detail.storeCount }} cửa hàng
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="downloadExcel(detail)"
          >
            <i class="far fa-file-excel mr-1"></i>Xuất Excel
          </b-button>
        </div>
        <b-table
          :items="detail.stores"
          :fields="storeFields"
          bordered
          small
          show-empty
          empty-text="Chương trình chưa có kỳ nào được chốt sổ"
        >
          <template v-slot:cell(storeName)="row">
            <span class="font-weight-bold">{{ row.item.storeName }}</span>
            <span v-if="row.item.storeShortName" class="text-muted">
              ({{ row.item.storeShortName }})</span
            >
          </template>
          <template v-slot:cell(periods)="row">
            <div
              v-for="period in row.item.periods"
              :key="period.settlementId"
              class="small"
            >
              {{ displayDate(period.fromDate) }} —
              {{ displayDate(period.toDate) }}:
              {{ displayAmount(period.amount) }}
            </div>
          </template>
          <template v-slot:cell(amount)="row">
            <span class="font-weight-bold">{{
              displayAmount(row.item.amount)
            }}</span>
          </template>
        </b-table>
      </template>
    </b-modal>
  </div>
</template>

<script>
/**
 * Dashboard thưởng AdHoc THEO CHƯƠNG TRÌNH: mỗi chương trình đã chi bao nhiêu, so với
 * ngân sách đã tới đâu, chi ở những cửa hàng nào; xuất Excel chia theo cửa hàng nhận.
 * "Đã chi" lấy từ các kỳ đã chốt sổ — cùng số với cột Ngân sách ở danh sách chương trình.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import xlsx from 'xlsx';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile } from '@/utils/common';
import {
  COMMISSION_ADHOC_STATUS_NAME,
  COMMISSION_ADHOC_STATUS_VARIANT,
  COMMISSION_ADHOC_BUDGET_STATUS,
  COMMISSION_ADHOC_BUDGET_STATUS_NAME,
  COMMISSION_ADHOC_BUDGET_STATUS_VARIANT,
  toOptionList,
} from './commission.constants';
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

const PAGE_LIMIT = 20;

export default {
  components: { KTCodePreview, VclTable },
  mixins: [commissionReportMixin],
  data() {
    return {
      PAGE_LIMIT,
      onLoading: false,
      programs: [],
      page: 1,
      totalItems: 0,
      totalPages: 0,
      filter: { keyword: '', status: null },
      statusOptions: toOptionList(COMMISSION_ADHOC_STATUS_NAME),
      detailVisible: false,
      detailLoading: false,
      detail: null,
      exportingId: null,
      fields: [
        { key: 'name', label: 'Chương trình', thStyle: thStyle('30%') },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'budget',
          label: 'Ngân sách',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'spentAmount',
          label: 'Đã chi',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        { key: 'usage', label: 'Mức dùng ngân sách', thStyle: thStyle('18%') },
        {
          key: 'actions',
          label: '',
          thStyle: thStyle('18%'),
          tdClass: 'text-center',
        },
      ],
      storeFields: [
        { key: 'storeName', label: 'Cửa hàng', thStyle: thStyle('40%') },
        { key: 'periods', label: 'Các kỳ đã chốt', thStyle: thStyle('40%') },
        {
          key: 'amount',
          label: 'Đã chi',
          thStyle: thStyle('20%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    summaryCards() {
      const withBudget = this.programs.filter((row) => Number(row.budget) > 0);
      const overCount = this.programs.filter(
        (row) =>
          Number(row.budgetStatus) === COMMISSION_ADHOC_BUDGET_STATUS.OVER,
      ).length;
      const nearCount = this.programs.filter(
        (row) =>
          Number(row.budgetStatus) === COMMISSION_ADHOC_BUDGET_STATUS.NEAR,
      ).length;
      const spent = this.programs.reduce(
        (sum, row) => sum + Number(row.spentAmount || 0),
        0,
      );
      const budget = withBudget.reduce(
        (sum, row) => sum + Number(row.budget || 0),
        0,
      );
      return [
        { key: 'count', label: 'Chương trình', value: this.programs.length },
        {
          key: 'spent',
          label: 'Tổng đã chi',
          value: this.displayAmount(spent),
        },
        {
          key: 'budget',
          label: 'Tổng ngân sách đã đặt',
          value: this.displayAmount(budget),
        },
        {
          key: 'near',
          label: 'Sắp hết ngân sách',
          value: nearCount,
          variant: 'warning',
        },
        {
          key: 'over',
          label: 'Vượt ngân sách',
          value: overCount,
          variant: 'danger',
        },
      ];
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng V2' },
      { title: 'Tổng hợp thưởng AdHoc theo chương trình' },
    ]);
    this.fetchPrograms();
  },
  methods: {
    statusName(status) {
      return COMMISSION_ADHOC_STATUS_NAME[status] || '';
    },
    statusVariant(status) {
      return COMMISSION_ADHOC_STATUS_VARIANT[status] || 'label-light-info';
    },
    budgetVariant(status) {
      return (
        COMMISSION_ADHOC_BUDGET_STATUS_VARIANT[Number(status)] || 'secondary'
      );
    },
    budgetStatusName(status) {
      return COMMISSION_ADHOC_BUDGET_STATUS_NAME[Number(status)] || '';
    },
    onPageChange(page) {
      this.page = page;
      this.$nextTick(() => this.fetchPrograms(false));
    },
    fetchPrograms(resetPage = true) {
      if (resetPage) {
        this.page = 1;
      }
      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-adhocs', {
        params: {
          page: this.page,
          limit: PAGE_LIMIT,
          keyword: this.filter.keyword || undefined,
          status: this.filter.status || undefined,
        },
      })
        .then(({ data }) => {
          this.programs = data.data.items || [];
          this.totalItems = data.data.pagination.totalItems || 0;
          this.totalPages = data.data.pagination.totalPages || 0;
        })
        .catch((error) => {
          this.programs = [];
          makeToastFaile(
            this.getErrorMessage(
              error,
              'Không tải được danh sách chương trình',
            ),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    fetchSpending(programId) {
      ApiService.setHeader();
      return ApiService.query(`commission-adhocs/${programId}/spending`).then(
        ({ data }) => data.data,
      );
    },
    openProgram(program) {
      this.detail = null;
      this.detailLoading = true;
      this.detailVisible = true;
      this.fetchSpending(program.id)
        .then((result) => {
          this.detail = result;
        })
        .catch((error) => {
          this.detailVisible = false;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được chi tiết chương trình'),
          );
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    exportProgram(program) {
      this.exportingId = program.id;
      this.fetchSpending(program.id)
        .then((result) => this.downloadExcel(result))
        .catch((error) => {
          makeToastFaile(this.getErrorMessage(error, 'Không xuất được Excel'));
        })
        .finally(() => {
          this.exportingId = null;
        });
    },
    /**
     * File Excel của một chương trình, chia theo cửa hàng nhận:
     * sheet 1 tổng theo cửa hàng, sheet 2 chi tiết từng kỳ đã chốt.
     */
    downloadExcel(spending) {
      const storeSheet = xlsx.utils.aoa_to_sheet([
        ['Chương trình', spending.name],
        [
          'Ngân sách',
          spending.budget ? Number(spending.budget) : 'Không giới hạn',
        ],
        ['Đã chi (đã chốt sổ)', spending.spentAmount],
        [],
        ['STT', 'Mã cửa hàng', 'Cửa hàng', 'Đã chi'],
        ...spending.stores.map((store, index) => [
          index + 1,
          store.storeShortName,
          store.storeName,
          store.amount,
        ]),
        [],
        ['', '', 'Tổng cộng', spending.spentAmount],
      ]);
      storeSheet['!cols'] = [{ wch: 8 }, { wch: 16 }, { wch: 40 }, { wch: 16 }];

      const periodRows = [];
      spending.stores.forEach((store) => {
        store.periods.forEach((period) => {
          periodRows.push([
            store.storeShortName,
            store.storeName,
            this.displayDate(period.fromDate),
            this.displayDate(period.toDate),
            period.amount,
          ]);
        });
      });
      const periodSheet = xlsx.utils.aoa_to_sheet([
        ['Mã cửa hàng', 'Cửa hàng', 'Từ ngày', 'Đến ngày', 'Đã chi'],
        ...periodRows,
      ]);
      periodSheet['!cols'] = [
        { wch: 16 },
        { wch: 40 },
        { wch: 12 },
        { wch: 12 },
        { wch: 16 },
      ];

      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, storeSheet, 'Theo cua hang');
      xlsx.utils.book_append_sheet(workbook, periodSheet, 'Theo ky chot');
      const safeName = String(spending.name || 'AdHoc')
        .replace(/[\\/:*?"<>|]/g, '')
        .slice(0, 60);
      xlsx.writeFile(workbook, `AdHoc_${spending.id}_${safeName}.xlsx`);
    },
  },
};
</script>

<style scoped>
.dash-filter {
  background: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  padding: 16px 20px;
}
.dash-filter__label {
  font-size: 12px;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 4px;
}
.dash-summary {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  overflow: hidden;
}
.dash-summary__card {
  padding: 14px 18px;
  border-right: 1px solid #ecf0f3;
}
.dash-summary__card:last-child {
  border-right: 0;
}
.dash-summary__card--warning .dash-summary__value {
  color: #b8860b;
}
.dash-summary__card--danger .dash-summary__value {
  color: #f64e60;
}
.dash-summary__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.dash-summary__value {
  font-size: 20px;
  font-weight: 700;
  color: #181c32;
}
.dash-link,
.dash-link:focus {
  color: #3699ff !important;
  font-weight: 500;
}
.dash-link:hover {
  color: #187de4 !important;
  text-decoration: underline;
}
</style>
